import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, AlertCircle, CheckCircle2, MailCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

export default function VerifyEmailPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState("");
  const [info, setInfo] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [resending, setResending] = React.useState(false);
  const [done, setDone] = React.useState(false);

  // If user becomes verified (from refresh elsewhere), bounce them away
  React.useEffect(() => {
    if (user && typeof user === "object" && user.email_verified) {
      navigate(`/dashboard/${user.role}`, { replace: true });
    }
  }, [user, navigate]);

  // If user is not logged in at all → go to login
  React.useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/auth/verify-email", { code });
      setDone(true);
      await refresh();
      setTimeout(() => navigate(`/dashboard/${user?.role || "customer"}`), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't verify. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setInfo("");
    setResending(true);
    try {
      await apiClient.post("/auth/resend-verification");
      setInfo("A fresh code has been sent to your inbox.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't resend. Try again shortly.");
    } finally {
      setResending(false);
    }
  };

  if (!user || typeof user !== "object") {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#475569]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Link to="/login" data-testid="verify-back-to-login" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold text-[#475569] hover:text-[#0A0A0A] mb-8">
          <ArrowLeft size={12} /> Sign out
        </Link>

        <div className="bg-white border border-[#0A0A0A]/10 p-10">
          {done ? (
            <div data-testid="verify-done" className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#5BB247]/10 border border-[#5BB247]/40 mb-6">
                <CheckCircle2 size={28} className="text-[#2c8642]" />
              </div>
              <h1 className="font-display text-2xl font-black tracking-tight text-[#0A0A0A]">Email verified</h1>
              <p className="mt-3 text-sm text-[#475569]">Sending you to your dashboard&hellip;</p>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#1E4D55]/10 border border-[#1E4D55]/20 mb-5">
                <MailCheck size={22} className="text-[#1E4D55]" />
              </div>
              <h1 className="font-display text-3xl font-black tracking-tight text-[#0A0A0A]">Confirm your email</h1>
              <p className="mt-3 text-sm text-[#475569] leading-relaxed">
                We sent a 6-digit code to <strong>{user.email}</strong>. Pop it in below to finish setting up your account.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="verify-form">
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
                    6-digit code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123 456"
                    data-testid="verify-code-input"
                    className="w-full px-4 py-4 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-2xl tracking-[0.4em] text-center font-mono"
                  />
                </div>

                {error && (
                  <div data-testid="verify-error" className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                {info && (
                  <div data-testid="verify-info" className="flex items-start gap-2 text-sm text-[#0A0A0A] bg-[#5BB247]/10 border border-[#5BB247]/40 p-3">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#2c8642]" />
                    <span>{info}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  data-testid="verify-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3.5 text-sm font-semibold transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                  {loading ? "Verifying&hellip;" : "Verify email"}
                </button>
              </form>

              <div className="mt-6 text-center text-xs text-[#475569]">
                Didn&apos;t get it?{" "}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  data-testid="verify-resend-button"
                  className="font-semibold text-[#1E4D55] hover:text-[#163C42] disabled:opacity-60"
                >
                  {resending ? "Sending&hellip;" : "Send a new code"}
                </button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
